import apiService from './apiService';

export type MtaStsMode = 'none' | 'testing' | 'enforce';
export type MtaStsCertStatus = 'pending' | 'issued' | 'failed';

export interface MtaStsExpectedRecord {
  name: string;
  type: string;
  value: string;
  note?: string;
}

export interface MtaStsExpectedDns {
  cname: MtaStsExpectedRecord;
  policy_txt: MtaStsExpectedRecord;
  tlsrpt_txt: MtaStsExpectedRecord;
}

export interface MtaStsCheck {
  ok: boolean | null;
  found: string | null;
  message: string | null;
}

export interface MtaStsChecks {
  cname: MtaStsCheck;
  policy_txt: MtaStsCheck;
  tlsrpt_txt: MtaStsCheck;
  https: MtaStsCheck;
}

export interface MtaStsPolicy {
  id: number;
  client_id: number | null;
  domain: string;
  policy_id: string;
  mode: MtaStsMode;
  mx_hosts: string[];
  max_age_seconds: number;
  enabled: boolean;
  last_validated_at: string | null;
  last_validation_ok: boolean | null;
  last_validation_message: string | null;
  last_validation_checks: MtaStsChecks | null;
  cert_status: MtaStsCertStatus;
  cert_last_issued_at: string | null;
  cert_last_error: string | null;
  created_at: string;
  updated_at: string;
  served_host: string;
  expected_dns: MtaStsExpectedDns;
}

export interface MtaStsPolicyInput {
  domain: string;
  client_id?: number | null;
  mode?: MtaStsMode;
  mx_hosts: string[];
  max_age_seconds?: number;
  enabled?: boolean;
}

// Use the shared apiService instance so requests get auth cookies, the
// X-CSRF-Token double-submit header on mutating requests, retry, and
// the standard error mapping. Each helper unwraps ApiResponse.data so
// callers see the route payload directly.
export const mtaStsApi = {
  list: () =>
    apiService
      .get<{ policies: MtaStsPolicy[] }>('/api/admin/mta-sts/policies')
      .then(r => r.data),
  getByDomain: (domain: string) =>
    apiService
      .get<MtaStsPolicy>(`/api/admin/mta-sts/policies/by-domain/${encodeURIComponent(domain)}`)
      .then(r => r.data),
  create: (input: MtaStsPolicyInput) =>
    apiService.post<MtaStsPolicy>('/api/admin/mta-sts/policies', input).then(r => r.data),
  update: (id: number, input: MtaStsPolicyInput) =>
    apiService
      .put<MtaStsPolicy>(`/api/admin/mta-sts/policies/${id}`, input)
      .then(r => r.data),
  remove: (id: number) =>
    apiService.delete(`/api/admin/mta-sts/policies/${id}`).then(r => r.data),
  validate: (id: number) =>
    apiService
      .post<MtaStsPolicy>(`/api/admin/mta-sts/policies/${id}/validate`)
      .then(r => r.data),
  // Plain DNS lookup for the 3 expected MTA-STS records — doesn't require a
  // stored policy. Used so the DNS-tab rows show check/X icons whether or not
  // MTA-STS is "enabled" in the portal. Returns the same shape as
  // MtaStsChecks, omitting `https` (only meaningful when we're serving it).
  checkDns: (domain: string) =>
    apiService
      .post<{ cname: MtaStsCheck; policy_txt: MtaStsCheck; tlsrpt_txt: MtaStsCheck }>(
        '/api/admin/mta-sts/check-dns',
        { domain }
      )
      .then(r => r.data),
};
